import { Box, Flex, Text } from '@chakra-ui/react';
import type { PortableTextBlock } from '@portabletext/types';
import type { RichTextResolverProp } from '@time-line/components/common';
import { Button, Divider } from '@time-line/components/common';
import type { Link, SanityImage } from '@time-line/components/types';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import type { ReactNode } from 'react';

interface PartnerPerkDrawerProps {
  image: SanityImage;
  title?: string;
  description?: PortableTextBlock[];
  shopUrl?: string;
  cta: Link;
  RichTextResolver?: RichTextResolverProp;
}

function DescriptionContainer({ children }: { children: ReactNode }) {
  return (
    <Box pb="m" sx={{ '> p.chakra-text + p.chakra-text': { mt: '3xs' } }}>
      {children}
    </Box>
  );
}

export function PartnerPerkDrawer({
  image,
  title,
  description,
  shopUrl,
  cta,
  RichTextResolver,
}: PartnerPerkDrawerProps) {
  const imageProps = useImage(image, { ratio: 240 / 300 });

  return (
    <Flex direction="column" height="100%" textAlign="center">
      <Box flexGrow={1}>
        {imageProps && (
          <Box
            width="240px"
            borderRadius="base"
            overflow="hidden"
            marginX="auto"
            mb="m"
          >
            <Image {...imageProps} />
          </Box>
        )}
        {title && (
          <Text as="h3" size="h3" mb="2xs" sx={{ textWrap: 'pretty' }}>
            {title}
          </Text>
        )}
        {description && RichTextResolver && (
          <RichTextResolver
            blocks={description}
            container={DescriptionContainer}
          />
        )}
      </Box>
      <Box position="sticky" bottom={0} bgColor="gray.50" pb="m">
        <Divider mb="sm" />
        <Button as="a" href={cta.href} width="100%" mb="sm">
          {cta.title}
        </Button>
        {shopUrl && (
          <Button
            href={shopUrl}
            as="a"
            variant="link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit partner shop
          </Button>
        )}
      </Box>
    </Flex>
  );
}
